import { useAppDispatch, useAppSelector } from '@app/store';
import {
  selectIsOrgSelected,
  selectOrganizationId,
  selectOrganizations,
  selectOrganizationsPageInfo,
  selectOrganizationsStatus,
} from '@app/store/selectors';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { formatPlanName } from '../utils';
import { ListLoadingSkelton } from './list-loading-skelton';

type OrganizationsListProps = {
  onSelectOrg: (org: {
    id: string;
    name: string;
    image: string;
    paymentPlan: string;
    planNameLocale: string;
    largeImage: string;
  }) => void;
  scrollableRoot?: HTMLElement | null;
};

export const OrganizationsList = ({
  onSelectOrg,
  scrollableRoot,
}: OrganizationsListProps) => {
  const dispatch = useAppDispatch();
  const organizations = useAppSelector(selectOrganizations);
  const currentOrganizationId = useAppSelector(selectOrganizationId);
  const organizationsStatus = useAppSelector(selectOrganizationsStatus);
  const isOrgSelected = useAppSelector(selectIsOrgSelected);
  const organizationsPageInfo = useAppSelector(selectOrganizationsPageInfo);
  const { t } = useTranslation();

  const { ref: loadingMoreRef, inView: loadingMoreInView } = useInView({
    root: scrollableRoot ?? null,
    rootMargin: '0px 0px 250px 0px',
    threshold: 0,
  });

  useEffect(() => {
    const shouldSkipLoading =
      !loadingMoreInView ||
      !organizationsPageInfo.hasNextPage ||
      organizationsStatus === 'processing';

    if (shouldSkipLoading) {
      return;
    }

    dispatch({
      type: '[nav-bar] organizations fetch',
    });
  }, [
    loadingMoreInView,
    organizationsPageInfo.hasNextPage,
    organizationsStatus,
    dispatch,
  ]);

  if (
    (organizations?.length === 0 && organizationsStatus !== 'processing') ||
    organizationsStatus === 'failed'
  ) {
    return null;
  }

  // If the user has only one org and is already in it, don't show the list
  if (
    isOrgSelected &&
    organizations?.length === 1 &&
    organizations[0].id === currentOrganizationId
  ) {
    return null;
  }

  return (
    <div role="group" aria-label={t('orgSwitcher.organizations')}>
      <div className="mb-2 text-xs font-inherit text-[rgb(105,110,124)]  uppercase tracking-wider">
        {t('orgSwitcher.organizations')}
      </div>
      {organizations?.map(org => {
        if (isOrgSelected && org.id === currentOrganizationId) {
          return null;
        }
        return (
          <button
            key={org.id}
            type="button"
            className="flex items-center bg-white text-left rounded font-medium mb-2 p-2 cursor-pointer w-full hover:bg-[rgb(236,243,248)] hover:text-[rgb(29,110,170)] min-h-[56px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            onClick={() =>
              onSelectOrg({
                id: org.id,
                name: org.name,
                image: org.image,
                paymentPlan: org.plan || '',
                planNameLocale: org.planNameLocale || '',
                largeImage: org.largeImage || '',
              })
            }
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelectOrg({
                  id: org.id,
                  name: org.name,
                  image: org.image,
                  paymentPlan: org.plan || '',
                  planNameLocale: org.planNameLocale || '',
                  largeImage: org.largeImage || '',
                });
              }
            }}
            role="menuitem"
            aria-label={t('orgSwitcher.accessibility.organizations', {
              organizationName: org.name,
            })}
            aria-describedby={org.plan ? `org-plan-${org.id}` : undefined}
          >
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium mr-3">
              {org.image ? (
                <img
                  src={org.image}
                  alt={org.name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                org.name.charAt(0).toUpperCase()
              )}
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900 max-w-[184px] truncate">
                {org.name}
              </div>
              {org.plan && (
                <div
                  id={`org-plan-${org.id}`}
                  className="text-xs text-[rgb(105,110,124)] font-normal max-w-[184px] truncate"
                >
                  {formatPlanName(
                    {
                      planName: org.plan,
                      planNameLocale: org.planNameLocale || '',
                    },
                    t,
                  )}
                </div>
              )}
            </div>
          </button>
        );
      })}
      {organizationsStatus === 'processing' ? (
        <div
          className="mb-2"
          role="status"
          aria-label={t('orgSwitcher.accessibility.loadingOrganizations')}
        >
          <ListLoadingSkelton />
          <ListLoadingSkelton />
          <ListLoadingSkelton />
          <ListLoadingSkelton />
          <ListLoadingSkelton />
        </div>
      ) : null}
      {organizationsPageInfo.hasNextPage &&
        organizationsStatus !== 'processing' && (
          <div className="h-4 w-full" ref={loadingMoreRef}></div>
        )}
    </div>
  );
};
